import type { NotifyCase } from "./types";

/** A server time (ISO, UTC) in the device's local time: "02:14" today, else
 * "30 Oct 23:58" (a night test crosses midnight). */
export function clockTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString([], { day: "numeric", month: "short" })} ${time}`;
}

/** A person by name, else by id (rows acked before the backend stored names
 * have only the id), with "(you)" for the signed-in person (`me`, their
 * `person_id` from `/me`). */
export function personName(
  personId: string,
  displayName: string | null | undefined,
  me: string | null,
): string {
  const name = displayName || personId;
  return personId === me ? `${name} (you)` : name;
}

/** The case owner as `personName` shows them; null while the case has none. */
export function ownerName(
  row: Pick<NotifyCase, "owner_person_id" | "owner_display_name">,
  me: string | null,
): string | null {
  return row.owner_person_id ? personName(row.owner_person_id, row.owner_display_name, me) : null;
}

/** The "Alert sent" value: its time, "not sent" when the alert failed (with
 * when, if `withFailedTime`), else "pending". */
export function alertSentText(
  row: Pick<NotifyCase, "alert_sent_at" | "alert_status" | "alert_failed_at">,
  { withFailedTime = false }: { withFailedTime?: boolean } = {},
  now?: Date,
): string {
  if (row.alert_sent_at) return clockTime(row.alert_sent_at, now);
  if (row.alert_status !== "failed") return "pending";
  return withFailedTime ? `not sent (${clockTime(row.alert_failed_at, now)})` : "not sent";
}
